import {
  submitSurveyResponses,
  type ScreenerQuestion,
  type SurveyResponseObject,
} from '@/modules/api';

import { getMemberContactQuestionField } from '../constants/programUnit';
import {
  MEMBER_CONTACT_FIELDS,
  type MemberContactField,
  type MemberContactFormValues,
} from '../schema/memberContactSchema';

import { createMemberContactInstance } from './createMemberContactInstance';

/**
 * Saves the member's contact info (ME-1256): creates the Member Contact
 * program-unit instance (see `createMemberContactInstance`) then PATCHes the
 * address/phone answers onto its questions, matched by UUID. Submitting the
 * instance is what writes the address/phone to the profile and checks off the
 * MWL checklist (backend side effects).
 *
 * Driven by our field list, not the backend's question order: every expected
 * field must have a matching question in the group or we throw.
 */
export async function submitMemberContact(
  userUuid: string,
  values: MemberContactFormValues
): Promise<void> {
  const instance = await createMemberContactInstance();
  const questionGroup = instance.programUnit?.contentCards?.[0]?.questionGroup;
  if (!questionGroup) {
    throw new Error('Member Contact program unit returned no question group.');
  }

  // Index the group's questions by the field each one collects, so we can build
  // the payload from our field list below and fail loudly on any missing field.
  const questionByField = new Map<MemberContactField, ScreenerQuestion>();
  for (const question of questionGroup.questions) {
    const field = getMemberContactQuestionField(question.uuid);
    if (field) questionByField.set(field, question);
  }

  const fieldValue = (field: MemberContactField): string => {
    if (field === 'phone') return values.phone.replace(/\D/g, '');
    return values[field] ?? '';
  };

  const objects: SurveyResponseObject[] = MEMBER_CONTACT_FIELDS.map((field) => {
    const question = questionByField.get(field);
    if (!question) {
      throw new Error(`Member Contact question group is missing the "${field}" question.`);
    }
    return {
      // `user` and `question` are resource URIs — Tastypie-style foreign-key
      // references to those records within the payload, not endpoints we call.
      // The questionGroup/instance URIs come straight off the fetched instance.
      user: `/api/v3/user/${userUuid}/`,
      question: `/api/v3/surveyquestion/${question.uuid}/`,
      questionGroup: questionGroup.resourceUri,
      answer: null,
      freeResponse: fieldValue(field),
      programUnitInstance: instance.resourceUri,
    };
  });

  await submitSurveyResponses({ objects });
}
